import axiosClient from "../axioxClient.ts/axiosClient";

export interface SearchResultItem {
  id: number;
  tableName: string; // 'cooperative_info' | 'notices' | 'complaints' | 'users'
  foundData: Record<string, any>;
}

export interface FormattedSearchResult {
  id: string | number;
  title: string;
  description: string;
  category: string;
  path: string;
  state?: Record<string, any>;
  rawItem?: SearchResultItem;
}

export const fetchSearchResults = async (
  query: string,
  limit: number = 10,
  tableName?: string,
): Promise<SearchResultItem[]> => {
  if (!query || query.trim().length < 2) {
    return [];
  }

  try {
    const params: Record<string, any> = {
      q: query.trim(),
      limit,
    };
    if (tableName) {
      params.tableName = tableName;
    }

    const response = await axiosClient.get<SearchResultItem[]>("/search", {
      params,
    });
    return response.data || [];
  } catch (error) {
    console.error("Error fetching search results:", error);
    return [];
  }
};

export const formatSearchResult = (
  item: SearchResultItem,
): FormattedSearchResult => {
  const { id, tableName, foundData } = item;

  switch (tableName) {
    case "cooperative_info": {
      const name =
        foundData?.nameEnglish || foundData?.nameNepali || `Cooperative #${id}`;
      const reg = foundData?.registrationNumber
        ? `Reg No: ${foundData.registrationNumber}`
        : "";
      const pan = foundData?.panNumber ? `PAN: ${foundData.panNumber}` : "";
      const mobileNum =
        foundData?.mobileNumber ||
        foundData?.phoneNumber ||
        foundData?.mobile ||
        foundData?.phone;
      const mobile = mobileNum ? `Mobile: ${mobileNum}` : "";
      const email = foundData?.email ? `Email: ${foundData.email}` : "";
      const details = [reg, pan, mobile, email].filter(Boolean).join(" | ");

      return {
        id: `coop-${id}`,
        title: name,
        description: details || "Cooperative Information",
        category: "Cooperative",
        path: `/cooperatives-list/${id}`,
        // path: "/cooperatives-list",
        // state: { selectedId: id, search: name },
        rawItem: item,
      };
    }

    case "notices": {
      const title = foundData?.noticeTitle || `Notice #${id}`;
      const type = foundData?.noticeType ? `Type: ${foundData.noticeType}` : "";
      const coopName = foundData?.cooperativeName
        ? (() => {
            const names =
              typeof foundData.cooperativeName === "object"
                ? Object.values(foundData.cooperativeName).join(", ")
                : String(foundData.cooperativeName);
            const words = names.split(/\s+/);
            return words.length > 8
              ? words.slice(0, 8).join(" ") + "..."
              : names;
          })()
        : "";
      const coop = coopName ? `Cooperative: ${coopName}` : "";
      const details = [coop, type].filter(Boolean).join(" | ");

      return {
        id: `notice-${id}`,
        title,
        description: details || "Notice details",
        category: "Notice",
        path: `/notices/edit/${id}`,
        // path: "/notices",
        // state: { selectedId: id, search: title },
        rawItem: item,
      };
    }

    case "complaints": {
      const complainant = foundData?.complainantName
        ? `Complaint by ${foundData.complainantName}`
        : foundData?.cooperativeName
          ? `Complaint against ${foundData.cooperativeName}`
          : `Complaint #${id}`;
      const coop = foundData?.cooperativeName
        ? `Cooperative: ${foundData.cooperativeName}`
        : "";
      const mobileNum =
        foundData?.mobileNumber || foundData?.phoneNumber || foundData?.phone;
      const mobile = mobileNum ? `Mobile: ${mobileNum}` : "";
      const email = foundData?.email ? `Email: ${foundData.email}` : "";
      const details = [coop, mobile, email].filter(Boolean).join(" | ");

      return {
        id: `complaint-${id}`,
        title: complainant,
        description: details || "Complaint Record",
        category: "Complaint",
        path: `/complaints/view/${id}`,
        // path: "/complaints-list",
        // state: { selectedId: id, search: complainant },
        rawItem: item,
      };
    }

    case "users": {
      const username =
        foundData?.userName ||
        [foundData?.firstName, foundData?.lastName].filter(Boolean).join(" ") ||
        `User #${id}`;
      const mobileNum =
        foundData?.phoneNumber || foundData?.mobileNumber || foundData?.phone;
      const mobile = mobileNum ? `Mobile: ${mobileNum}` : "";
      const email = foundData?.email ? `Email: ${foundData.email}` : "";
      const details = [mobile, email].filter(Boolean).join(" | ");

      return {
        id: `user-${id}`,
        title: username,
        description: details || email || "User Profile",
        category: "User",
        path: `/user/view/${id}`,
        rawItem: item,
      };
    }

    default:
      return {
        id: `item-${id}`,
        title: `Item #${id}`,
        description: tableName,
        category: tableName,
        path: "/",
        rawItem: item,
      };
  }
};
